import { Link } from "react-router-dom";
import { Trophy } from "@phosphor-icons/react";
import { fileUrl } from "../../lib/api";
import { useLang } from "../../i18n";
import Chapter from "./Chapter";
import Reveal from "./Reveal";

const MEDAL = {
    gold: { label: "Emas", cls: "text-[#B8890B] border-[#FFB800]", order: 0 },
    silver: { label: "Perak", cls: "text-racing-muted border-racing-muted", order: 1 },
    bronze: { label: "Perunggu", cls: "text-[#B0763F] border-[#B0763F]", order: 2 },
};

const LEVEL_ORDER = ["Internasional", "Nasional", "Provinsi", "Kota/Kabupaten", "Internal Klub"];

function levelRank(value) {
    const index = LEVEL_ORDER.indexOf((value || "").trim());
    return index === -1 ? LEVEL_ORDER.length : index;
}

export default function Prestasi({ achievements, athletes = [], compact = false }) {
    const { t } = useLang();

    const eventMap = {};
    achievements.forEach((achievement) => {
        const eventName = (achievement.event || "").trim() || "Event Lainnya";
        if (!eventMap[eventName]) eventMap[eventName] = { name: eventName, items: [] };
        eventMap[eventName].items.push(achievement);
    });

    const eventGroups = Object.values(eventMap).map((group) => {
        group.items.sort((a, b) =>
            (b.year || 0) - (a.year || 0)
            || levelRank(a.level) - levelRank(b.level)
            || (a.category || "").localeCompare(b.category || "")
            || (MEDAL[a.medal]?.order ?? 9) - (MEDAL[b.medal]?.order ?? 9)
            || (a.athlete || "").localeCompare(b.athlete || "")
        );
        return group;
    }).sort((a, b) => {
        const latestA = Math.max(...a.items.map((item) => item.year || 0));
        const latestB = Math.max(...b.items.map((item) => item.year || 0));
        return latestB - latestA || a.name.localeCompare(b.name);
    });

    const athleteLink = (name, testid) => {
        const profile = athletes.find((p) => p.name === name);
        return profile ? (
            <Link to={`/atlet/${profile.id}`} data-testid={testid} className="hover:text-racing-red transition-colors duration-300">
                {name}
            </Link>
        ) : (
            name
        );
    };

    const renderItem = (achievement, groupIndex, itemIndex) => {
        const medal = MEDAL[achievement.medal] || MEDAL.gold;
        const testid = `prestasi-item-${groupIndex}-${itemIndex}`;
        return (
            <article key={achievement.id || `${achievement.athlete}-${achievement.title}`} className="flex items-start gap-4 p-4 md:px-5 border-b border-racing-line last:border-0" data-testid={testid}>
                <span className={`mt-1 shrink-0 inline-block text-[10px] uppercase tracking-[0.2em] border px-2.5 py-1 ${medal.cls}`}>{medal.label}</span>
                <div className="min-w-0 flex-1">
                    <h4 className="font-display uppercase text-lg leading-tight text-neutral-900 truncate">{athleteLink(achievement.athlete, `${testid}-athlete`)}</h4>
                    <p className="text-sm text-neutral-800 mt-1 line-clamp-2">{achievement.title}</p>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-racing-muted mt-2">
                        {achievement.category || "Lainnya"}{achievement.level ? ` · ${achievement.level}` : ""}{achievement.year ? ` · ${achievement.year}` : ""}
                    </p>
                </div>
                {achievement.image_url && (
                    <img src={fileUrl(achievement.image_url)} alt={achievement.title} className="hidden sm:block w-16 h-16 object-cover border border-racing-line shrink-0" />
                )}
            </article>
        );
    };

    const renderEvent = (group, groupIndex) => {
        const years = [...new Set(group.items.map((item) => item.year).filter(Boolean))].sort((a, b) => b - a);
        return (
            <div key={group.name} data-testid={`prestasi-event-group-${groupIndex}`}>
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 bg-racing-red px-5 py-4 sticky top-0">
                    <Trophy size={18} weight="duotone" className="text-racing-gold self-center" />
                    <h3 className="font-display uppercase text-xl md:text-2xl text-white" data-testid={`prestasi-event-title-${groupIndex}`}>
                        {group.name}
                    </h3>
                    <span className="text-[10px] uppercase tracking-[0.25em] text-white/70" data-testid={`prestasi-event-count-${groupIndex}`}>
                        {group.items.length} {t.ch_prestasi_t}{years.length ? ` · ${years.join(", ")}` : ""}
                    </span>
                </div>
                <div>
                    {group.items.map((achievement, itemIndex) => renderItem(achievement, groupIndex, itemIndex))}
                </div>
            </div>
        );
    };

    const totalItems = achievements.length;

    return (
        <section id="prestasi" className="py-24 md:py-32 border-t border-racing-line" data-testid="prestasi-section">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <Chapter number="04" title={t.ch_prestasi_t} subtitle={t.ch_prestasi_s} />

                {totalItems === 0 ? (
                    <p className="text-racing-muted" data-testid="prestasi-empty">{t.prestasi_empty}</p>
                ) : compact ? (
                    <div className="max-w-4xl">
                        <div className="relative">
                            <div className="border border-racing-line bg-white max-h-[480px] overflow-y-auto" data-lenis-prevent data-testid="prestasi-compact-list">
                                {eventGroups.map((group, groupIndex) => renderEvent(group, groupIndex))}
                            </div>
                            {totalItems > 3 && (
                                <>
                                    <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white via-white/75 to-transparent" />
                                    <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 bg-white/90 border border-racing-line px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-racing-muted">
                                        +{Math.max(totalItems - 3, 0)} prestasi lainnya
                                    </div>
                                </>
                            )}
                        </div>
                        <Link
                            to="/prestasi"
                            data-testid="prestasi-see-more"
                            className="mt-5 inline-block border border-racing-red text-racing-red hover:bg-racing-red hover:text-white text-xs uppercase tracking-[0.25em] px-6 py-3 transition-colors duration-300"
                        >
                            Lihat Lainnya
                        </Link>
                    </div>
                ) : (
                    <Reveal>
                        <div className="border border-racing-line bg-white" data-testid="prestasi-full-list">
                            {eventGroups.map((group, groupIndex) => renderEvent(group, groupIndex))}
                        </div>
                    </Reveal>
                )}
            </div>
        </section>
    );
}
